/**
 * Color. Cubre las dos formas en que los modulos lo declaran:
 *
 * - `char` con `#RRGGBB` (`products.attribute_value.html_color`): selector nativo
 *   de color mas la caja de texto, porque el codigo exacto se copia y se pega.
 * - `integer` como indice de una paleta (`services.tag.color`): botones de la
 *   paleta, porque no es un color libre sino una de N opciones y un selector de
 *   16 millones de colores sobre un indice de 0 a 11 mentiria sobre lo que se
 *   puede guardar.
 *
 * La paleta sale de los tokens del sistema de diseno, no de hexadecimales
 * sueltos: los mismos colores tienen que funcionar en tema claro y oscuro.
 */

import clsx from 'clsx';

import { controlClass } from '@/components/fields/controls';
import type { FieldInputProps } from '@/components/fields/types';

/**
 * Indice -> color. El 0 es el neutro, como dice la ayuda del campo.
 *
 * Van como valor CSS y no como clase de Tailwind para no arrastrar la paleta por
 * defecto de Tailwind a un sistema de diseno que solo usa tokens propios. Son
 * doce matices con la misma luminosidad, elegidos para leerse sobre fondo claro
 * y oscuro sin cambiar de valor.
 */
export const PALETA_TABLERO: string[] = [
  'rgb(var(--surface-2))',
  '#2f9e8f',
  '#3b82c4',
  '#7c6ad6',
  '#b45fb0',
  '#c9556e',
  '#cf6a3a',
  '#b08a25',
  '#6f9c3a',
  '#3fa08a',
  '#5b8fa8',
  '#8a7fa0',
];

const HEX = /^#[0-9a-fA-F]{6}$/;

export function ColorField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  // ------------------------------------------------------------------ paleta
  if (meta.type === 'integer') {
    const indice = typeof value === 'number' ? value : 0;
    if (readonly) {
      return (
        <output id={id} className="flex items-center gap-2 py-1.5 text-sm">
          <span
            aria-hidden="true"
            className="h-5 w-5 rounded-full border border-line"
            style={{ backgroundColor: PALETA_TABLERO[indice] ?? PALETA_TABLERO[0] }}
          />
          <span className="text-fg-muted">{indice === 0 ? 'Sin color' : `Color ${indice}`}</span>
        </output>
      );
    }
    return (
      <div
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        aria-describedby={describedBy}
        className="flex flex-wrap gap-1.5 py-1"
      >
        {PALETA_TABLERO.map((color, posicion) => (
          <button
            key={color}
            type="button"
            role="radio"
            aria-checked={indice === posicion}
            aria-label={posicion === 0 ? 'Sin color' : `Color ${posicion}`}
            onClick={() => {
              onChange(posicion);
            }}
            style={{ backgroundColor: color }}
            className={clsx(
              'h-7 w-7 rounded-full border transition-transform hover:scale-110',
              indice === posicion ? 'border-fg ring-2 ring-accent/40' : 'border-line',
            )}
          />
        ))}
      </div>
    );
  }

  // --------------------------------------------------------------------- hex
  const texto = typeof value === 'string' ? value : '';
  const valido = HEX.test(texto);

  if (readonly) {
    return (
      <output id={id} className="flex items-center gap-2 py-1.5 text-sm" aria-describedby={describedBy}>
        {valido ? (
          <>
            <span
              aria-hidden="true"
              className="h-5 w-5 rounded border border-line"
              style={{ backgroundColor: texto }}
            />
            <span className="font-mono tabular-nums">{texto}</span>
          </>
        ) : (
          <span className="text-fg-faint">—</span>
        )}
      </output>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        // El selector nativo no acepta vacio: sin valor arranca en negro, y eso
        // no es "sin color" sino "negro". Se muestra blanco y solo escribe cuando
        // el usuario efectivamente elige.
        value={valido ? texto : '#ffffff'}
        aria-label={`${meta.label}: elegir`}
        onChange={(evento) => {
          onChange(evento.target.value);
        }}
        className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-line bg-surface p-1"
      />
      <input
        id={id}
        type="text"
        inputMode="text"
        placeholder="#RRGGBB"
        maxLength={7}
        className={controlClass(invalid, 'font-mono')}
        value={texto}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        autoComplete="off"
        spellCheck={false}
        onChange={(evento) => {
          onChange(evento.target.value === '' ? '' : evento.target.value);
        }}
      />
    </div>
  );
}
